import React, { useState } from "react";
import {
  EllipsisVertical,
  Eye,
  LucideFile,
  Share,
  UploadCloud,
} from "lucide-react";
import moment from "moment";
import { toast } from "sonner";
import { useNavigate, useParams } from "react-router-dom";
import { useAppDispatch, useAppSelector } from "src/redux/hooks";

import Popover from "src/components/common/Popover";
import Button from "src/components/app/Button";
import DocumentSelectBottomSheet from "../bottom-sheets/DocumentSelectBottomSheet";

import environment from "src/constants/environment";
import {
  DOCUMENT_REVIEW_STATUS,
  POST_MESSAGE_EVENTS,
} from "src/constants/common";
import { encryptData } from "src/helpers/encrypt-decrypt";
import * as actions from "src/redux/slices/programSlice";
import { PROGRAM_STATUS } from "src/constants/program";
import { sendMessageToParent } from "src/helpers/common";
import { ProgramRecordType } from "src/types/program.types";

interface ProgramDocumentCardProps {
  className?: string;
  docCardClassName?: string;
  documentData: any;
  documentType: string;
  document: ProgramRecordType;
  // Milestone key ("M1", "M2", …) when the upload belongs to a program
  // milestone. Onboarding uploads leave it unset.
  milestone?: string;
  onSuccess: () => void;
  onError?: () => void;
  onUploadingStart?: () => void;
}

const ProgramDocumentCard: React.FC<ProgramDocumentCardProps> = ({
  className = "bg-gray-100",
  docCardClassName,
  documentData,
  documentType,
  document,
  milestone,
  onSuccess,
  onError,
  onUploadingStart,
}) => {
  const dispatch = useAppDispatch();
  const { uploadProgramRecords } = useAppSelector((state) => state.program);
  const { patient } = useAppSelector((state) => state.auth);
  const [showDocumentUploadBottomSheet, setShowDocumentUploadBottomSheet] =
    useState(false);
  const [showUploadAgainBottomSheet, setShowUploadAgainBottomSheet] =
    useState(false);

  const handleFileUpload = async ({
    fileData = [],
    documentDetails,
  }: {
    fileData: any;
    documentDetails: any;
  }) => {
    onUploadingStart && onUploadingStart();
    setShowDocumentUploadBottomSheet(false);
    setShowUploadAgainBottomSheet(false);
    if (!fileData || (fileData && fileData?.length === 0)) {
      toast.error("Please select a file to upload");
      return;
    }
    // setIsFileUploading(true);
    try {
      // setShowUploadSheet(false);

      const formData = new FormData();

      let payload = {
        ...(showUploadAgainBottomSheet && {
          test_type_id: document?.test_type_id,
          patient_records_id: document?.patient_records_id,
        }),
        title: documentData?.document_type,
        // title: documentDetails.documentName,
        // test_name: documentDetails.documentType,
        test_name: documentData?.document_type,
        container_account: environment.AZURE_CONTAINER_ACCOUNT,
        document_data: [
          // uploadAgain
          //   ? document?.document_name || ""
          //   : `${
          //       (patient?.data as any)?.patient_id
          //     }-New-Record-${moment().unix()}.pdf`,
          showUploadAgainBottomSheet
            ? document?.document_name || ""
            : `${
                (patient?.data as any)?.patient_id
              }-New-Record-${moment().unix()}.pdf`,
        ],
        container_name: "patient-reports",
        extension_type:
          fileData?.[0]?.type !== "application/pdf" ? "jpg" : "pdf",
        meta_data: {
          report_status: DOCUMENT_REVIEW_STATUS.UNDER_REVIEW,
          [documentType]: "1",
          // Only milestone uploads carry this — it tags the record to the
          // milestone it unlocks.
          ...(milestone && { milestone }),
          // ...(document?.visit_id && { visit_id: document?.visit_id }),
        },
      };

      // Handle file upload
      const files = Array.isArray(fileData) ? fileData : [fileData];

      // Append files to FormData
      for (const file of files) {
        formData.append("file", file);
      }

      formData.append("payload", encryptData(payload));

      new Promise((resolve, reject) => {
        dispatch(
          actions.uploadProgramRecordsRequest({
            payload: formData,
            resolve,
            reject,
          }),
          // recordsActions.updateHealthRecordRequest({
          //   payload: formData,
          //   resolve,
          //   reject,
          // })
        );
      })
        .then((res) => {
          // setShowSuccess(true);
          // setCurrentPage(1);
          // fetchHealthRecords();
          // setIsFileUploading(false);
          setShowDocumentUploadBottomSheet(false);
          setShowUploadAgainBottomSheet(false);
          onSuccess();

          toast.success("Record uploaded successfully");
        })
        .catch((error) => {
          console.error("Error while uploading record", error);
          // setIsFileUploading(false);
          setShowDocumentUploadBottomSheet(false);
          setShowUploadAgainBottomSheet(false);
          onError?.();
          toast.error("Failed to upload record");
        });
    } catch (error) {
      console.error("Error while uploading Health Record", error);
      setShowDocumentUploadBottomSheet(false);
      setShowUploadAgainBottomSheet(false);
      toast.error("Failed to upload record");
      // setIsFileUploading(false);
    }
  };

  return (
    <div
      key={documentData?.id}
      className={`border rounded-xl p-4 border-gray-300  ${className}`}
    >
      <p className="text-[var(--text-primary)] font-semibold">
        {documentData?.title}
      </p>
      <p className="text-gray-500 mt-2 mb-4">{documentData?.description}</p>
      {document ? (
        <DocumentCard
          className={docCardClassName}
          document={document}
          showUploadAgain={
            documentData?.uploadedDocument?.record_meta_info?.report_status ===
              DOCUMENT_REVIEW_STATUS.REJECTED ||
            document?.record_meta_info?.report_status ===
              DOCUMENT_REVIEW_STATUS.REJECTED
          }
          onUploadAgain={() => setShowUploadAgainBottomSheet(true)}
        />
      ) : (
        <div>
          <Button
            variant="contained"
            color="primary"
            rounded="full"
            fullWidth={true}
            onClick={() => setShowDocumentUploadBottomSheet(true)}
            loading={uploadProgramRecords.isLoading}
            disabled={uploadProgramRecords.isLoading}
          >
            Upload
          </Button>
          <p className="text-sm text-gray-500 text-center mt-2">
            Supported: {documentData?.supported_file_types.join(", ")} (Max{" "}
            {documentData?.max_file_size_in_mb} MB)
          </p>
        </div>
      )}

      {(showDocumentUploadBottomSheet || showUploadAgainBottomSheet) && (
        <DocumentSelectBottomSheet
          show={showDocumentUploadBottomSheet || showUploadAgainBottomSheet}
          handleClose={() => {
            setShowDocumentUploadBottomSheet(false);
            setShowUploadAgainBottomSheet(false);
          }}
          onFileUpload={handleFileUpload}
          documentName={documentData?.document_type}
        />
      )}
    </div>
  );
};

export default ProgramDocumentCard;

const DocumentCard: React.FC<{
  className?: string;
  document: ProgramRecordType;
  showUploadAgain?: boolean;
  onUploadAgain: () => void;
}> = ({
  className = "bg-white",
  document,
  showUploadAgain = false,
  onUploadAgain,
}) => {
  const navigate = useNavigate();
  const params = useParams();
  const supportProgramKey = params["support-program-key"];

  const getDocumentUrl = (document: any) => {
    return Array.isArray(document.document_url) &&
      document.document_url.length > 0
      ? document.document_url[0]
      : Array.isArray(document.imageURL) && document.imageURL.length > 0
        ? document.imageURL[0]
        : document.imageURL;
  };

  const handleShare = (document: any) => {
    const payload = {
      documentLink: getDocumentUrl(document),
      documentName: document?.title,
    };
    sendMessageToParent(POST_MESSAGE_EVENTS.SHARE_RECORD_DOCUMENT, payload);
  };

  const handleViewFile = (document: any) => {
    const payload = {
      document_name: document.document_name,
      title: document.title,
      document_url: getDocumentUrl(document),
    };
    navigate(`/app/${supportProgramKey}/records/preview`, {
      state: {
        record: payload,
      },
    });
  };

  return (
    <div
      className={`flex flex-col gap-2 border border-gray-300 rounded-lg p-2 w-full ${className}`}
    >
      <div className="flex items-center gap-2 w-full">
        <div className="bg-gray-200 p-2 rounded-md flex items-center justify-center">
          <LucideFile className="w-4 h-4" />
        </div>
        <div className="flex flex-col gap-1 flex-1">
          <p className="text-sm text-black line-clamp-2">{document?.title}</p>
          {/* <p className="text-sm text-gray-500">Size: 1.2 MB</p> */}
          <p className="text-sm text-gray-500">
            Status:{" "}
            <span
              className={`${
                PROGRAM_STATUS[
                  document?.record_meta_info
                    ?.report_status as keyof typeof PROGRAM_STATUS
                ]?.color
              } text-white px-2 py-1 rounded-md text-xs`}
            >
              {
                PROGRAM_STATUS[
                  document?.record_meta_info
                    ?.report_status as keyof typeof PROGRAM_STATUS
                ]?.label
              }
            </span>
          </p>
        </div>
        {/* <button className="text-red-500 underline w-10 h-10 flex items-center justify-center rounded-md active:bg-gray-200 flex-shrink-0">
        <Trash2 className="w-4 h-4" />
      </button> */}
        <Popover label={<EllipsisVertical className="w-5 h-5" />}>
          <ul className="w-full">
            <li className="w-full block">
              <button
                type="button"
                className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                onClick={() => handleViewFile(document)}
              >
                <Eye className="w-4 h-4" /> View File
              </button>
            </li>

            <li className="w-full block">
              <button
                type="button"
                className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                onClick={() => handleShare(document)}
              >
                <Share className="w-4 h-4" /> Share File
              </button>
            </li>

            {showUploadAgain && (
              <li className="w-full block">
                <button
                  type="button"
                  className="flex items-center gap-2 text-sm w-full p-2 hover:bg-gray-100 rounded-md"
                  onClick={onUploadAgain}
                >
                  <UploadCloud className="w-4 h-4" /> Upload Again
                </button>
              </li>
            )}
          </ul>
        </Popover>
      </div>
      {(document as any)?.record_meta_info?.rejected_reason && (
        <p className="text-xs font-semibold">
          Reason:{" "}
          <span className="text-gray-500 font-normal">
            {(document as any)?.record_meta_info?.rejected_reason}
          </span>
        </p>
      )}
    </div>
  );
};
